/**
 * Personal Access Token hooks. Backed by /api/v1/me/tokens (Keycloak-auth).
 * Hand-written against the existing axios client; like workspace.ts, gracefully
 * tolerates 404 from older backends so the Settings page renders empty rather
 * than crashing.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';

export interface ApiToken {
  id: string;
  userId: string;
  userEmail?: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
}

export interface CreateApiTokenRequest {
  name: string;
  scopes?: string[];
  /** ISO 8601 timestamp; omit for non-expiring. */
  expiresAt?: string;
}

export interface CreateApiTokenResponse {
  /** Persisted record. */
  token: ApiToken;
  /** Raw token shown once at creation. Caller must surface it immediately. */
  raw: string;
}

const tokensQueryKey = ['me', 'tokens'] as const;

function useTokens() {
  return useQuery({
    queryKey: tokensQueryKey,
    queryFn: async (): Promise<ApiToken[]> => {
      try {
        const { data } = await client.instance.get<{ data?: ApiToken[] } | ApiToken[]>(
          '/me/tokens',
        );
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

function useCreateToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateApiTokenRequest): Promise<CreateApiTokenResponse> => {
      const { data } = await client.instance.post<CreateApiTokenResponse>(
        '/me/tokens',
        body,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tokensQueryKey }),
  });
}

function useRevokeToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/me/tokens/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tokensQueryKey }),
  });
}

export const tokensApi = {
  useList: useTokens,
  useCreate: useCreateToken,
  useRevoke: useRevokeToken,
};
